/**
 * MarkdownPastePlugin — gère le collage de contenu Markdown.
 *
 * Problème résolu : par défaut, Lexical lit la version HTML du clipboard.
 * Quand on copie depuis VS Code (ou tout autre éditeur de code avec syntax
 * highlighting), cette version HTML contient des balises <pre><code> ou des
 * <span style="color: ..."> qui font perdre les marqueurs Markdown ou les
 * transforment en CodeNodes. Résultat : la mise en forme est cassée.
 *
 * Solution : intercepter PASTE_COMMAND avec une priorité haute pour court-
 * circuiter le handler par défaut. On ne lit QUE le `text/plain` du
 * clipboard, et on parse ce texte comme Markdown via $convertFromMarkdownString.
 *
 *   - Si l'éditeur est vide (root sans texte) → parsing direct, le contenu
 *     parsé remplace le root.
 *   - Si l'éditeur a déjà du contenu → buffer technique : on parse dans le
 *     root (clear/parse/snapshot), on restaure le contenu original, et on
 *     append les nœuds parsés à la fin du root. UX : le contenu collé arrive
 *     à la fin, l'utilisateur peut le déplacer ensuite.
 *
 * Notes implémentation :
 *
 *   - L'accès à `clipboardData` se fait via cast direct (pas d'instanceof)
 *     car certains navigateurs / Lexical wrappent l'event différemment et
 *     `event instanceof ClipboardEvent` peut renvoyer false à tort.
 *
 *   - On utilise PRIORITY HIGH pour passer avant le handler par défaut de
 *     RichTextPlugin (qui est à PRIORITY EDITOR).
 */

import { useEffect } from "react";
import {
  $getRoot,
  $getSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_HIGH,
  PASTE_COMMAND,
} from "lexical";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $convertFromMarkdownString, TRANSFORMERS } from "@lexical/markdown";

import { TABLE_TRANSFORMER } from "./tableTransformer";

// Même set que MarkdownPlugin : transformers de base + GFM tableau.
const PASTE_TRANSFORMERS = [TABLE_TRANSFORMER, ...TRANSFORMERS];

export function MarkdownPastePlugin() {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    return editor.registerCommand(
      PASTE_COMMAND,
      (event) => {
        // Accès direct à la propriété, sans instanceof : plus permissif
        // (couvre ClipboardEvent natifs, synthétiques React, et certains
        // wrappers où instanceof échoue).
        const clipboardData =
          (event as ClipboardEvent | null)?.clipboardData ??
          (event as { dataTransfer?: DataTransfer | null } | null)?.dataTransfer ??
          null;

        if (!clipboardData) {
          // Pas de clipboard accessible → on laisse passer au handler par défaut
          return false;
        }

        const text = clipboardData.getData("text/plain");
        if (!text) {
          // Pas de texte brut (probablement une image ou autre) → handler par défaut
          return false;
        }

        // À partir d'ici on prend la main complètement.
        if (event && typeof (event as Event).preventDefault === "function") {
          (event as Event).preventDefault();
        }

        editor.update(() => {
          const root = $getRoot();

          // Test d'éditeur "vide" basé sur le texte total — plus simple et
          // plus tolérant que de compter les enfants (couvre 1 paragraphe
          // vide, 0 enfants, ou même plusieurs paragraphes vides).
          const isEmpty = root.getTextContent().trim() === "";

          if (isEmpty) {
            // Cas simple : parsing direct, le contenu remplace le root.
            $convertFromMarkdownString(text, PASTE_TRANSFORMERS);
            return;
          }

          // Cas non vide : buffer technique pour préserver le contenu existant.
          //
          // 1. Snapshot des enfants actuels
          // 2. Clear root + parse markdown dedans
          // 3. Snapshot des nœuds parsés
          // 4. Clear root + restore l'original
          // 5. Append les nœuds parsés à la fin
          //
          // Le détachement par .remove() puis ré-append n'invalide PAS les
          // références aux nœuds (ils sont juste déplacés). En revanche la
          // sélection est perdue ; on la repositionne sur le premier nœud
          // parsé.
          const savedChildren = root.getChildren();

          root.clear();
          $convertFromMarkdownString(text, PASTE_TRANSFORMERS);
          const parsedChildren = root.getChildren();

          // Détache les parsedChildren avant de restaurer
          for (const child of parsedChildren) {
            child.remove();
          }

          // Restaure l'original
          for (const child of savedChildren) {
            root.append(child);
          }

          // Append les parsés à la fin
          for (const child of parsedChildren) {
            root.append(child);
          }

          // Re-positionne le caret sur le premier nœud collé pour confort
          const firstPasted = parsedChildren[0];
          if (firstPasted && "selectStart" in firstPasted) {
            (firstPasted as unknown as { selectStart: () => void }).selectStart();
          } else {
            const selection = $getSelection();
            if ($isRangeSelection(selection)) {
              root.selectEnd();
            }
          }
        });

        return true;
      },
      COMMAND_PRIORITY_HIGH,
    );
  }, [editor]);

  return null;
}
